import { useTranslation } from 'react-i18next';
import React, { ChangeEvent, useCallback, useEffect } from 'react';

import styles from './CustomerInputFields.module.scss';
import { TextField } from './TextField';
import { CUSTOMER_INFO_TEST_ID } from 'components/ContactInfoForm.const';
import { useContactInfoFormState } from 'components/ContactInfoForm';
import { PhoneInputField } from './PhoneInputField/PhoneInputField';
import { useCustomer } from 'hooks/context/customer/customer';
import { isPhoneNumberValid } from 'util/Formatting';
import { Customer } from 'olo-service';
import { validateEmail } from 'util/Regex';
import { useMerchant } from 'hooks/context/merchant';

export const CustomerInputFields: React.FC = () => {
  const { t } = useTranslation();

  const { customer: globalCustomer } = useCustomer();

  const {
    setCustomer,
    customer: { firstName, lastName, email, phoneNumber, country },
  } = useContactInfoFormState();

  const { country: merchantCountry } = useMerchant();

  const setCustomerFieldByTarget = useCallback(({ target }: ChangeEvent<HTMLInputElement>) => {
    if (!target.name) {
      if (process.env.NODE_ENV === 'development') console.error(`no name for ${target} found`);

      return;
    }

    const { name, value } = target;
    const key = name as keyof Customer;

    setCustomer({ key, value });
  }, [setCustomer]);

  const validatePhoneNumber = useCallback((value?: string) => isPhoneNumberValid(value, country), [country]);

  useEffect(() => {
    if (merchantCountry === 'US') {
      setCustomer({ key: 'country', value: merchantCountry });
    }
  }, [merchantCountry, setCustomer]);

  return (
    <>
      <TextField
        className={styles.inputField}
        errorMessage={t('contactForm.emptyField')}
        label={t('contactForm.firstName')}
        labelClass={styles.label}
        onChange={setCustomerFieldByTarget}
        required={true}
        name="firstName"
        value={firstName}
        disabled={!!globalCustomer?.firstName}
        data-testid={CUSTOMER_INFO_TEST_ID.firstName}
      />

      <TextField
        className={styles.inputField}
        errorMessage={t('contactForm.emptyField')}
        label={t('contactForm.lastName')}
        labelClass={styles.label}
        onChange={setCustomerFieldByTarget}
        required={true}
        name="lastName"
        value={lastName}
        disabled={!!globalCustomer?.lastName}
        data-testid={CUSTOMER_INFO_TEST_ID.lastName}
      />

      {merchantCountry === 'US' ? (
        <TextField
          className={styles.inputField}
          label={t('contactForm.phoneNumber')}
          labelClass={styles.label}
          name="phoneNumber"
          onChange={setCustomerFieldByTarget}
          required={true}
          type="tel"
          validateField={(value) => validatePhoneNumber(value) || t<string>('contactForm.invalidPhone')}
          value={phoneNumber}
          disabled={globalCustomer?.phoneNumber?.verified}
          data-testid={CUSTOMER_INFO_TEST_ID.phoneNumber}
        />
      ) : (
        <PhoneInputField
          className={styles.inputField}
          label={t('contactForm.phoneNumber')}
          labelClass={styles.label}
          value={phoneNumber}
          onChange={setCustomer}
          onValidate={validatePhoneNumber}
          required={true}
          errorMessage={t('contactForm.invalidPhone')}
        />
      )}

      <TextField
        className={styles.inputField}
        label={t('contactForm.email')}
        labelClass={styles.label}
        onChange={setCustomerFieldByTarget}
        required={true}
        type="email"
        name="email"
        validateField={(value) => validateEmail(value) || t<string>('contactForm.invalidEmail')}
        value={email}
        disabled={globalCustomer?.emailAddress?.verified}
        data-testid={CUSTOMER_INFO_TEST_ID.emailAddress}
      />
    </>
  );
};
